import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, photoUrl, formatDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GENDERS = ["Woman", "Man", "Non-binary", "Prefer not to say"];

export default function ProfileSetup() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState(user?.name || "");
    const [age, setAge] = useState(user?.age || "");
    const [gender, setGender] = useState(user?.gender || "");
    const [city, setCity] = useState(user?.city || "");
    const [budgetMin, setBudgetMin] = useState(user?.budget_min || 500);
    const [budgetMax, setBudgetMax] = useState(user?.budget_max || 1200);
    const [moveIn, setMoveIn] = useState(user?.move_in_date || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [photos, setPhotos] = useState(user?.photos || []);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setPhotos((p) => [...p, data.id]);
            }
            toast.success("Photos added");
        } catch (err) {
            toast.error(formatDetail(err));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removePhoto = async (id) => {
        try {
            await api.delete(`/photos/${id}`);
            setPhotos((p) => p.filter((x) => x !== id));
        } catch (err) {
            toast.error(formatDetail(err));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name || !age || !gender || !city || !budgetMax || !moveIn) {
            toast.error("Fill all required fields");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put("/profile", {
                name,
                age: Number(age),
                gender,
                city,
                budget_min: Number(budgetMin),
                budget_max: Number(budgetMax),
                move_in_date: moveIn,
                bio,
                photos,
            });
            setUser(data);
            toast.success("Profile saved");
            navigate("/onboarding/vibe");
        } catch (err) {
            toast.error(formatDetail(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative min-h-screen bg-background overflow-hidden pb-16">
            <div className="blob bg-primary" style={{ width: 260, height: 260, top: -60, right: -50 }} />
            <div className="grain absolute inset-0" />
            <div className="relative z-10 mx-auto max-w-2xl px-6 py-10">
                <div className="flex items-center gap-2 mb-4 text-xs font-semibold text-accent-foreground bg-accent px-3 py-1.5 rounded-full w-fit">
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} /> Step 1 of 2
                </div>
                <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl font-black tracking-tighter">
                    Show us your <span className="font-fancy text-gradient-purple">vibe</span>
                </motion.h1>
                <p className="mt-2 text-muted-foreground">This helps roommates & flats find you. You can edit anytime.</p>

                <form onSubmit={submit} className="mt-8 space-y-6">
                    {/* Photos */}
                    <div className="rounded-3xl border border-border bg-card p-6">
                        <Label className="text-base font-semibold">Photos</Label>
                        <p className="text-xs text-muted-foreground mt-1">Add at least 1. First one is your main pic.</p>
                        <div className="mt-4 grid grid-cols-3 gap-3">
                            {photos.map((id, i) => (
                                <div key={id} className="relative aspect-square rounded-2xl overflow-hidden border border-border group" data-testid={`photo-${i}`}>
                                    <img src={photoUrl(id)} alt="me" className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(id)}
                                        data-testid={`remove-photo-${i}`}
                                        className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            {photos.length < 6 && (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="add-photo-btn"
                                    className="aspect-square rounded-2xl border-2 border-dashed border-border grid place-items-center hover:border-primary hover:bg-accent lift"
                                >
                                    <div className="flex flex-col items-center text-muted-foreground">
                                        <Camera className="h-6 w-6" strokeWidth={2.5} />
                                        <span className="text-xs mt-1">{uploading ? "Uploading…" : "Add"}</span>
                                    </div>
                                </button>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={handleUpload}
                                data-testid="photo-file-input"
                            />
                        </div>
                    </div>

                    {/* Name & Age */}
                    <div className="rounded-3xl border border-border bg-card p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name-input" className="mt-2 h-12 rounded-2xl" />
                            </div>
                            <div>
                                <Label htmlFor="age">Age</Label>
                                <Input id="age" type="number" min={18} max={99} value={age} onChange={(e) => setAge(e.target.value)} required data-testid="profile-age-input" className="mt-2 h-12 rounded-2xl" />
                            </div>
                        </div>

                        <div>
                            <Label>Gender</Label>
                            <RadioGroup value={gender} onValueChange={setGender} className="mt-2 flex flex-wrap gap-2">
                                {GENDERS.map((g) => (
                                    <label
                                        key={g}
                                        className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-medium lift ${gender === g ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background hover:bg-accent"}`}
                                        data-testid={`gender-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                    >
                                        <RadioGroupItem value={g} className="sr-only" />
                                        {g}
                                    </label>
                                ))}
                            </RadioGroup>
                        </div>

                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} required placeholder="Mumbai, Bengaluru, Delhi…" data-testid="profile-city-input" className="mt-2 h-12 rounded-2xl" />
                        </div>
                    </div>

                    {/* Budget */}
                    <div className="rounded-3xl border border-border bg-card p-6 space-y-4">
                        <Label className="text-base font-semibold">Budget (monthly, ₹)</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="bmin" className="text-xs">Min</Label>
                                <Input id="bmin" type="number" min={0} value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} data-testid="profile-budget-min-input" className="mt-1 h-12 rounded-2xl" />
                            </div>
                            <div>
                                <Label htmlFor="bmax" className="text-xs">Max</Label>
                                <Input id="bmax" type="number" min={0} required value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} data-testid="profile-budget-max-input" className="mt-1 h-12 rounded-2xl" />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="movein">Move-in date</Label>
                            <Input id="movein" type="date" required value={moveIn} onChange={(e) => setMoveIn(e.target.value)} data-testid="profile-movein-input" className="mt-2 h-12 rounded-2xl" />
                        </div>
                    </div>

                    {/* Bio */}
                    <div className="rounded-3xl border border-border bg-card p-6">
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A sentence about the human you are." rows={4} data-testid="profile-bio-input" className="mt-2 rounded-2xl" />
                    </div>

                    <Button type="submit" disabled={saving} data-testid="profile-save-btn" className="h-14 w-full rounded-2xl text-base font-semibold lift">
                        {saving ? "Saving…" : (<>Next: Vibe Quiz <ArrowRight className="ml-2 h-5 w-5" /></>)}
                    </Button>
                </form>
            </div>
        </div>
    );
}
